// ════════════════════════════════════════════════════════════════════
// TERROR — THE MUSIC PLAYER (the little ♪ in the corner)
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-30: "I want the user to be able to click a music icon
// and options appear: pause, play, next, back, mute. No scrubbing, just
// easy next song or last song."
//
// Click the round ♪ → a small tray opens: back · pause/play · next ·
// mute, plus the name of what is playing. Click ♪ again (or press
// Escape) to tuck it away.
//
// ── MOUNT ──────────────────────────────────────────────────────────
// App.js is a ladder of early `return`s, so a <MusicPlayer /> element
// would have to be pasted into every branch (the exact reason the
// soundtrack itself is a hook — see Soundtrack.js). Instead this mounts
// ONCE into its own <div> on document.body via mountMusicPlayer(),
// called from one effect in App. Safe against StrictMode's double
// mount: the host div is looked up by id first.
//
// ALL behaviour lives in Soundtrack.js (next/back/pause/mute). This file
// is buttons and a label. Nothing here touches Firebase.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  usePlayerState, togglePaused, nextTrack, prevTrack, toggleMuted,
} from "./Soundtrack";

const GOLD = "#c9a24b";
const INK = "rgba(14,10,8,0.92)";

const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d={d} />
  </svg>
);
// Plain SVG paths (no emoji — emoji draw differently on every device).
const P = {
  note:  "M9 3v11.3A3.5 3.5 0 1 0 11 17.5V7h7V3H9z",
  prev:  "M6 5h2v14H6V5zm3.5 7L19 5v14l-9.5-7z",
  next:  "M16 5h2v14h-2V5zM5 5l9.5 7L5 19V5z",
  play:  "M7 4.5v15l13-7.5-13-7.5z",
  pause: "M6 4h4v16H6V4zm8 0h4v16h-4V4z",
  vol:   "M3 9v6h4l5 4V5L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z",
  mute:  "M3 9v6h4l5 4V5L7 9H3zm13.6 3l2.4-2.4-1.4-1.4-2.4 2.4-2.4-2.4-1.4 1.4 2.4 2.4-2.4 2.4 1.4 1.4 2.4-2.4 2.4 2.4 1.4-1.4-2.4-2.4z",
};

const btn = (active) => ({
  width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  background: active ? "rgba(201,162,75,0.22)" : "transparent",
  border: `1px solid ${active ? GOLD : "rgba(201,162,75,0.35)"}`,
  color: GOLD, padding: 0,
});

function MusicPlayer() {
  const [open, setOpen] = useState(false);
  const { title, paused, muted } = usePlayerState();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const silent = paused || muted;

  return (
    <div style={{
      position: "fixed", left: 12, bottom: 12, zIndex: 9000,
      display: "flex", alignItems: "center", gap: 8,
      fontFamily: "Georgia, serif", userSelect: "none",
    }}>
      <button
        type="button"
        aria-label={open ? "Hide music controls" : "Show music controls"}
        aria-expanded={open}
        title="Music"
        onClick={() => setOpen((o) => !o)}
        style={{ ...btn(open), width: 40, height: 40, background: INK, opacity: silent ? 0.7 : 1 }}
      >
        <Icon d={silent ? P.mute : P.note} />
      </button>

      {open && (
        <div
          role="group"
          aria-label="Music controls"
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
            background: INK, border: "1px solid rgba(201,162,75,0.45)", borderRadius: 24,
          }}
        >
          <button type="button" aria-label="Previous song" title="Back" onClick={prevTrack} style={btn(false)}>
            <Icon d={P.prev} />
          </button>
          <button type="button" aria-label={paused ? "Play" : "Pause"} title={paused ? "Play" : "Pause"} onClick={togglePaused} style={btn(paused)}>
            <Icon d={paused ? P.play : P.pause} />
          </button>
          <button type="button" aria-label="Next song" title="Next" onClick={nextTrack} style={btn(false)}>
            <Icon d={P.next} />
          </button>
          <button type="button" aria-label={muted ? "Unmute" : "Mute"} title={muted ? "Unmute" : "Mute"} onClick={toggleMuted} style={btn(muted)}>
            <Icon d={muted ? P.mute : P.vol} />
          </button>
          <span style={{
            color: GOLD, fontSize: 12, letterSpacing: 1, maxWidth: 130,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 4px",
          }}>
            {title || "—"}
          </span>
        </div>
      )}
    </div>
  );
}

export default MusicPlayer;

const HOST_ID = "ie-music-player";
let root = null;

/** Mount the player once, on document.body. Idempotent. */
export function mountMusicPlayer() {
  if (typeof document === "undefined") return;
  let host = document.getElementById(HOST_ID);
  if (host && root) return;
  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    document.body.appendChild(host);
  }
  root = createRoot(host);
  root.render(<MusicPlayer />);
}
